import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Truck, Shield, Loader2, Lock, Package, AlertTriangle, Tag, X, CheckCircle, Wallet, MapPin, Search } from 'lucide-react';
import { listingsAPI, photoAPI, ordersAPI, shippingAPI, promoAPI, offersAPI, walletAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import AddressAutocomplete from '../components/AddressAutocomplete';

const CheckoutPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(true);
  const [shippingForm, setShippingForm] = useState({
    shipping_name: '', shipping_address: '', shipping_city: '',
    shipping_postal_code: '', shipping_country: 'FR', shipping_phone: '',
  });
  const [addressQuery, setAddressQuery] = useState('');
  const [shippingZone, setShippingZone] = useState('metropole');
  const [sellerNoAddress, setSellerNoAddress] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [acceptedOffer, setAcceptedOffer] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  // Service point (relay) selection
  const [servicePoints, setServicePoints] = useState([]);
  const [selectedServicePoint, setSelectedServicePoint] = useState(null);
  const [loadingServicePoints, setLoadingServicePoints] = useState(false);
  const [servicePointSearch, setServicePointSearch] = useState('');
  const [servicePointTestMode, setServicePointTestMode] = useState(false);
  const [servicePointError, setServicePointError] = useState(null);
  const [needsPostal, setNeedsPostal] = useState(false);

  const EU_COUNTRIES = ['FR', 'ES', 'IT', 'BE', 'PT', 'DE', 'NL', 'LU', 'AT', 'IE', 'PL', 'SE', 'DK', 'FI', 'CZ', 'SK', 'SI', 'HR', 'HU', 'RO', 'BG', 'GR', 'EE', 'LV', 'LT', 'CY', 'MT'];
  const countryName = (code) => {
    try { return new Intl.DisplayNames([i18n.language], { type: 'region' }).of(code) || code; } catch { return code; }
  };
  const [shippingUnavailable, setShippingUnavailable] = useState(false);

  const fetchShippingRates = useCallback((sellerId, toPostalCode, toCountry = 'FR') => {
    setLoadingShipping(true);
    setShippingUnavailable(false);
    shippingAPI.getRates({
      to_country: toCountry || 'FR',
      to_postal_code: toPostalCode || '',
      weight: 0.6,
      seller_id: sellerId,
    }).then(ratesRes => {
      setShippingOptions(ratesRes.data.shipping_options);
      setNeedsPostal(!!ratesRes.data.needs_postal);
      setShippingUnavailable(!!ratesRes.data.unavailable);
      if (ratesRes.data.shipping_options.length > 0) setSelectedShipping(ratesRes.data.shipping_options[0]);
      else setSelectedShipping(null);
      if (ratesRes.data.seller_has_address === false) setSellerNoAddress(true);
      setShippingZone(ratesRes.data.zone || 'metropole');
    }).catch(() => {}).finally(() => setLoadingShipping(false));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    listingsAPI.getOne(id)
      .then(res => {
        setListing(res.data);
        setLoading(false);
        const sellerId = res.data?.seller?.id || res.data?.seller_id;
        const uc = (user?.country || 'FR').toUpperCase();
        const initCountry = EU_COUNTRIES.includes(uc) ? uc : 'FR';
        setShippingForm(prev => ({ ...prev, shipping_country: initCountry }));
        fetchShippingRates(sellerId, '', initCountry);
      })
      .catch(() => { toast.error(t('co.not_found')); navigate('/catalogue'); });
    // Fetch accepted offer for this listing
    offersAPI.getAccepted(id).then(res => {
      if (res.data.accepted_offer) setAcceptedOffer(res.data.accepted_offer);
    }).catch(() => {});
    // Fetch wallet balance
    walletAPI.get().then(res => {
      setWalletBalance(res.data.balance || 0);
    }).catch(() => {});
  }, [id, isAuthenticated, navigate, fetchShippingRates]);

  const handleAddressSelect = (addr) => {
    setShippingForm(prev => ({
      ...prev,
      shipping_address: addr.address,
      shipping_city: addr.city,
      shipping_postal_code: addr.postal_code,
    }));
    // Recalculate shipping when postal code changes
    if (listing && addr.postal_code) {
      const sellerId = listing?.seller?.id || listing?.seller_id;
      fetchShippingRates(sellerId, addr.postal_code, shippingForm.shipping_country);
      // Reset service point selection when address changes
      setSelectedServicePoint(null);
      setServicePoints([]);
    }
  };

  // Fetch service points for the selected carrier (Boxtal or Packlink dropoffs)
  const fetchServicePoints = useCallback((postalCode, carrier = 'mondial_relay', packlinkServiceId = null) => {
    if (!postalCode || postalCode.length < 4) return;
    setLoadingServicePoints(true);
    setServicePointTestMode(false);
    setServicePointError(null);
    const request = packlinkServiceId
      ? shippingAPI.getPacklinkDropoffs(postalCode, packlinkServiceId, shippingForm.shipping_country)
      : shippingAPI.getServicePoints(postalCode, carrier);
    request
      .then(res => {
        setServicePoints(res.data.service_points || []);
        if (res.data.test_mode) {
          setServicePointTestMode(true);
        }
        if (res.data.error) {
          setServicePointError(res.data.error_message || 'Erreur inconnue');
          console.error('Service points API error:', res.data);
        }
      })
      .catch((err) => {
        console.error('Service points error:', err);
        setServicePoints([]);
        setServicePointError('Erreur de connexion');
      })
      .finally(() => setLoadingServicePoints(false));
  }, [shippingForm.shipping_country]);

  // Check if selected shipping requires a service point
  const requiresServicePoint = selectedShipping?.requires_service_point || 
                               selectedShipping?.name?.toLowerCase().includes('relais') || 
                               selectedShipping?.name?.toLowerCase().includes('point');

  // Get the carrier for service points (colissimo or mondial_relay)
  const getServicePointCarrier = () => {
    if (selectedShipping?.service_point_carrier) return selectedShipping.service_point_carrier;
    if (selectedShipping?.carrier?.toLowerCase().includes('mondial')) return 'mondial_relay';
    if (selectedShipping?.carrier?.toLowerCase().includes('colissimo')) return 'colissimo';
    return 'mondial_relay';
  };

  // Load service points when a relay option is selected
  useEffect(() => {
    if (requiresServicePoint && shippingForm.shipping_postal_code) {
      setSelectedServicePoint(null);
      setServicePoints([]);
      if (selectedShipping?.provider === 'packlink') {
        fetchServicePoints(shippingForm.shipping_postal_code, null, selectedShipping.id);
      } else {
        fetchServicePoints(shippingForm.shipping_postal_code, getServicePointCarrier());
      }
    } else if (!requiresServicePoint) {
      setSelectedServicePoint(null);
      setServicePoints([]);
    }
  }, [requiresServicePoint, shippingForm.shipping_postal_code, selectedShipping?.id]);

  const retryServicePoints = () => {
    if (selectedShipping?.provider === 'packlink') {
      fetchServicePoints(shippingForm.shipping_postal_code, null, selectedShipping.id);
    } else {
      fetchServicePoints(shippingForm.shipping_postal_code, getServicePointCarrier());
    }
  };

  const shippingCost = selectedShipping?.price || 0;
  const effectivePrice = acceptedOffer ? acceptedOffer.amount : (listing?.price || 0);
  const promoDiscount = promoResult?.discount_amount || 0;
  const discountedPrice = listing ? Math.max(effectivePrice - promoDiscount, 0) : 0;
  const protectionFee = listing ? Math.round((0.50 + discountedPrice * 0.04) * 100) / 100 : 0;
  const totalPrice = listing ? discountedPrice + shippingCost + protectionFee : 0;
  const walletUsed = useWallet ? Math.min(walletBalance, totalPrice) : 0;
  const stripeAmount = Math.round((totalPrice - walletUsed) * 100) / 100;

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await promoAPI.validate(promoCode.trim(), effectivePrice);
      setPromoResult(res.data);
      toast.success(t('co.promo_success', { amount: res.data.discount_amount.toFixed(2) }));
    } catch (err) {
      setPromoResult(null);
      toast.error(err.response?.data?.detail || t('co.promo_invalid'));
    }
    setPromoLoading(false);
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoResult(null);
  };

  const isFormValid = shippingForm.shipping_name && shippingForm.shipping_address &&
    shippingForm.shipping_city && shippingForm.shipping_postal_code && shippingForm.shipping_phone &&
    selectedShipping && (!requiresServicePoint || selectedServicePoint || servicePointTestMode);

  const handlePay = async () => {
    if (!isFormValid) { 
      if (requiresServicePoint && !selectedServicePoint) {
        toast.error(t('co.select_point'));
      } else {
        toast.error(t('co.fill_fields')); 
      }
      return; 
    }
    setBuying(true);
    try {
      const res = await ordersAPI.create({
        listing_id: id,
        ...shippingForm,
        shipping_method_id: selectedShipping.id,
        shipping_method_name: selectedShipping.name,
        shipping_cost: selectedShipping.price,
        promo_code: promoResult?.code || null,
        frontend_origin: window.location.origin,
        wallet_amount: walletUsed,
        // Service point info for Mondial Relay
        service_point_id: requiresServicePoint && selectedServicePoint ? String(selectedServicePoint.id) : null,
        service_point_name: requiresServicePoint ? (selectedServicePoint?.name || null) : null,
        service_point_address: requiresServicePoint ? (selectedServicePoint?.formatted_address || null) : null,
      });
      if (res.data.wallet_paid) {
        // Fully paid with wallet — go to success page
        navigate(`/order/success/${res.data.order_id}?wallet=true`);
      } else {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('co.purchase_error'));
      setBuying(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7FAFB]">
      <div className="w-12 h-12 border-4 border-[#0C63BF] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!listing) return null;

  const photoUrl = listing.photos?.[0] ? photoAPI.getUrl(listing.photos[0]) : null;
  const modelName = listing.model?.replace(new RegExp('^' + listing.brand + '\\s+', 'i'), '') || listing.model;

  return (
    <div className="min-h-screen bg-[#F0F7FA]">
      <div className="bg-[#021E47] py-4">
        <div className="max-w-3xl mx-auto px-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-white/70 hover:text-white transition-colors" data-testid="checkout-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold text-white" data-testid="checkout-title">{t('co.title')}</h1>
          <Lock className="w-4 h-4 text-[#32CD32] ml-auto" />
          <span className="text-xs text-[#32CD32] font-medium">{t('co.secure_badge')}</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Left column: forms */}
          <div className="lg:col-span-3 space-y-5">
            {/* Product recap */}
            <div className="bg-white rounded-2xl border border-[#E8F4FD] p-4 flex gap-4" data-testid="checkout-product">
              <div className="w-20 h-20 rounded-xl bg-[#F0F7FA] overflow-hidden shrink-0">
                {photoUrl ? (
                  <img src={photoUrl} alt={modelName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#B8D4E3]"><Package className="w-8 h-8" /></div>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-[#5A8BA8] uppercase tracking-wider font-semibold">{listing.brand}</p>
                <p className="font-bold text-[#033871] text-sm line-clamp-1">{modelName}</p>
                {acceptedOffer ? (
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-lg font-black text-emerald-600" data-testid="checkout-product-negotiated">{acceptedOffer.amount.toFixed(2)}&euro;</p>
                    <p className="text-xs text-[#B8D4E3] line-through">{listing.price.toFixed(2)}&euro;</p>
                    <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">Prix négocié</span>
                  </div>
                ) : (
                  <p className="text-lg font-black text-[#0C63BF] mt-1">{listing.price.toFixed(2)}&euro;</p>
                )}
              </div>
            </div>

            {/* Shipping address */}
            <div className="bg-white rounded-2xl border border-[#E8F4FD] p-5" data-testid="checkout-address">
              <h2 className="font-bold text-[#033871] text-sm mb-4 flex items-center gap-2">
                <Truck className="w-4 h-4 text-[#0C63BF]" /> {t('co.ship_address')}
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-[#5A8BA8] block mb-1">{t('co.country')}</label>
                  <select
                    value={shippingForm.shipping_country}
                    onChange={e => {
                      const c = e.target.value;
                      setShippingForm(prev => ({ ...prev, shipping_country: c }));
                      setSelectedServicePoint(null);
                      setServicePoints([]);
                      if (listing && shippingForm.shipping_postal_code.length >= 4) {
                        const sellerId = listing?.seller?.id || listing?.seller_id;
                        fetchShippingRates(sellerId, shippingForm.shipping_postal_code, c);
                      } else {
                        setShippingOptions([]);
                        setSelectedShipping(null);
                        setNeedsPostal(true);
                      }
                    }}
                    className="w-full h-11 px-3 rounded-xl border border-[#D6E8F0] text-sm bg-white focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none transition-colors"
                    data-testid="checkout-country">
                    {EU_COUNTRIES.map(c => <option key={c} value={c}>{countryName(c)}</option>)}
                  </select>
                </div>
                <input placeholder={t('co.full_name')} value={shippingForm.shipping_name}
                  onChange={e => setShippingForm(prev => ({ ...prev, shipping_name: e.target.value }))}
                  className="w-full h-11 px-4 rounded-xl border border-[#D6E8F0] text-sm bg-white focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none transition-colors"
                  data-testid="checkout-name" />
                {shippingForm.shipping_country === 'FR' ? (
                  <AddressAutocomplete
                    value={addressQuery}
                    onChange={setAddressQuery}
                    onSelect={handleAddressSelect}
                    placeholder={t('co.type_address')}
                    label={t('co.ship_address')}
                    required
                  />
                ) : (
                  <input placeholder={t('co.type_address')} value={shippingForm.shipping_address}
                    onChange={e => setShippingForm(prev => ({ ...prev, shipping_address: e.target.value }))}
                    className="w-full h-11 px-4 rounded-xl border border-[#D6E8F0] text-sm bg-white focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none transition-colors"
                    data-testid="checkout-address-input" />
                )}
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder={t('co.postal_code')} value={shippingForm.shipping_postal_code}
                    onChange={e => {
                      const val = e.target.value;
                      setShippingForm(prev => ({ ...prev, shipping_postal_code: val }));
                      if (val.length >= 4 && listing) {
                        const sellerId = listing?.seller?.id || listing?.seller_id;
                        fetchShippingRates(sellerId, val, shippingForm.shipping_country);
                      }
                    }}
                    className="w-full h-11 px-4 rounded-xl border border-[#D6E8F0] text-sm bg-white focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none transition-colors"
                    data-testid="checkout-postal" />
                  <input placeholder={t('co.city')} value={shippingForm.shipping_city}
                    onChange={e => setShippingForm(prev => ({ ...prev, shipping_city: e.target.value }))}
                    className="w-full h-11 px-4 rounded-xl border border-[#D6E8F0] text-sm bg-white focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none transition-colors"
                    data-testid="checkout-city" />
                </div>
                {shippingZone !== 'metropole' && (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-200">
                    <AlertTriangle className="w-4 h-4 text-blue-500 shrink-0" />
                    <p className="text-xs text-blue-700">
                      {shippingZone === 'dom_tom' ? t('co.dom_tom') : t('co.corsica')}
                    </p>
                  </div>
                )}
                <input placeholder={t('co.phone')} value={shippingForm.shipping_phone}
                  onChange={e => setShippingForm(prev => ({ ...prev, shipping_phone: e.target.value }))}
                  type="tel" required
                  className={`w-full h-11 px-4 rounded-xl border text-sm bg-white focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none transition-colors ${!shippingForm.shipping_phone ? 'border-red-300' : 'border-[#D6E8F0]'}`}
                  data-testid="checkout-phone" />
              </div>
            </div>

            {/* Shipping method */}
            <div className="bg-white rounded-2xl border border-[#E8F4FD] p-5" data-testid="checkout-shipping">
              <h2 className="font-bold text-[#033871] text-sm mb-4 flex items-center gap-2">
                <Truck className="w-4 h-4 text-[#0C63BF]" /> {t('co.ship_mode')}
              </h2>
              {loadingShipping ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-[#0C63BF]" />
                  <span className="ml-2 text-sm text-[#5A8BA8]">{t('co.loading_options')}</span>
                </div>
              ) : sellerNoAddress ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="seller-no-address-warning">
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                  <p className="text-sm text-amber-700">Le vendeur n'a pas encore renseigné son adresse d'enlèvement. Les frais de livraison sont estimatifs.</p>
                </div>
              ) : null}
              {!loadingShipping && shippingOptions.length > 0 ? (
                <div className="space-y-2">
                  {shippingOptions.map(opt => (
                    <label key={opt.id}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedShipping?.id === opt.id
                          ? 'border-[#0C63BF] bg-[#F0F7FA]'
                          : 'border-[#E8F4FD] hover:border-[#B8D4E3]'
                      }`} data-testid={`checkout-ship-${opt.id}`}>
                      <input type="radio" name="shipping" checked={selectedShipping?.id === opt.id}
                        onChange={() => setSelectedShipping(opt)} className="accent-[#0C63BF]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#033871]">{opt.name}</p>
                        <p className="text-[11px] text-[#5A8BA8]">{opt.carrier} &middot; {opt.delay}</p>
                      </div>
                      <span className="text-sm font-bold text-[#0C63BF]">{opt.price.toFixed(2)}&euro;</span>
                    </label>
                  ))}
                </div>
              ) : !loadingShipping && needsPostal ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-[#F0F7FA] border border-[#D6E8F0]" data-testid="shipping-needs-postal">
                  <Truck className="w-5 h-5 text-[#0C63BF] shrink-0" />
                  <p className="text-sm text-[#3D5870]">{t('co.needs_postal')}</p>
                </div>
              ) : !loadingShipping ? (
                <p className="text-sm text-[#5A8BA8] text-center py-4" data-testid="no-shipping-options">
                  {shippingUnavailable ? t('co.intl_unavailable') : t('co.no_options')}
                </p>
              ) : null}
            </div>

            {/* Service Point Selection for Mondial Relay */}
            {requiresServicePoint && shippingForm.shipping_postal_code && (
              <div className="bg-white rounded-2xl border border-[#E8F4FD] p-5" data-testid="checkout-service-point">
                <h2 className="font-bold text-[#033871] text-sm mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#0C63BF]" /> {t('co.choose_point')}
                </h2>
                
                {loadingServicePoints ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-[#0C63BF]" />
                    <span className="ml-2 text-sm text-[#5A8BA8]">{t('co.searching_points')}</span>
                  </div>
                ) : servicePoints.length > 0 ? (
                  <>
                    {/* Search/filter */}
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8D4E3]" />
                      <input 
                        type="text"
                        value={servicePointSearch}
                        onChange={(e) => setServicePointSearch(e.target.value)}
                        placeholder={t('co.filter_points')}
                        className="w-full h-9 pl-9 pr-3 rounded-xl border border-[#D6E8F0] text-xs focus:border-[#0C63BF] outline-none"
                        data-testid="service-point-search"
                      />
                    </div>
                    
                    {/* Service points list */}
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {servicePoints
                        .filter(sp => 
                          !servicePointSearch || 
                          sp.name?.toLowerCase().includes(servicePointSearch.toLowerCase()) ||
                          sp.city?.toLowerCase().includes(servicePointSearch.toLowerCase())
                        )
                        .map(sp => (
                        <label key={sp.id}
                          className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            selectedServicePoint?.id === sp.id
                              ? 'border-[#32CD32] bg-emerald-50'
                              : 'border-[#E8F4FD] hover:border-[#B8D4E3]'
                          }`} data-testid={`service-point-${sp.id}`}>
                          <input 
                            type="radio" 
                            name="servicePoint" 
                            checked={selectedServicePoint?.id === sp.id}
                            onChange={() => setSelectedServicePoint(sp)} 
                            className="accent-[#32CD32] mt-1" 
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#033871]">{sp.name}</p>
                            <p className="text-[11px] text-[#5A8BA8]">{sp.formatted_address}</p>
                            {sp.distance && (
                              <p className="text-[10px] text-[#0C63BF] mt-1">
                                {t('co.at_km', { km: (sp.distance / 1000).toFixed(1) })}
                              </p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                    
                    {selectedServicePoint && (
                      <div className="mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                        <p className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5" /> {t('co.point_selected')}
                        </p>
                        <p className="text-xs text-emerald-600 mt-1">{selectedServicePoint.name}</p>
                        <p className="text-[10px] text-emerald-600">{selectedServicePoint.formatted_address}</p>
                      </div>
                    )}
                  </>
                ) : servicePointTestMode ? (
                  <div className="text-center py-4 px-3">
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 mb-3">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-medium text-amber-700">{t('co.test_mode')}</span>
                    </div>
                    <p className="text-sm text-[#5A8BA8]">{t('co.point_unavailable')}</p>
                  </div>
                ) : servicePointError ? (
                  <div className="text-center py-4 px-3">
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 mb-3">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="text-xs font-medium text-red-700">{t('co.api_error')}</span>
                    </div>
                    <p className="text-sm text-red-600">{servicePointError}</p>
                    <button 
                      onClick={retryServicePoints}
                      className="mt-3 text-xs text-[#0C63BF] hover:underline"
                    >
                      {t('co.retry')}
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-[#5A8BA8]">{t('co.no_points')}</p>
                    <button 
                      onClick={retryServicePoints}
                      className="mt-2 text-xs text-[#0C63BF] hover:underline"
                    >
                      {t('co.retry')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right column: order summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-[#E8F4FD] p-5 lg:sticky lg:top-24" data-testid="checkout-summary">
              <h2 className="font-bold text-[#033871] text-sm mb-4">{t('co.summary')}</h2>

              {/* Promo code */}
              <div className="mb-4">
                {promoResult ? (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="promo-applied">
                    <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-emerald-700">{t('co.promo_applied', { code: promoResult.code })}</p>
                      <p className="text-[10px] text-emerald-600">{t('co.promo_discount', { amount: promoResult.discount_amount.toFixed(2) })}</p>
                    </div>
                    <button onClick={handleRemovePromo} className="text-emerald-600 hover:text-red-500 transition-colors" data-testid="promo-remove-btn">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#B8D4E3]" />
                      <input value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())}
                        placeholder={t('co.promo_code')}
                        onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
                        className="w-full h-9 pl-8 pr-3 rounded-xl border border-[#D6E8F0] text-xs font-bold uppercase focus:border-[#0C63BF] outline-none"
                        data-testid="promo-input" />
                    </div>
                    <button onClick={handleApplyPromo} disabled={promoLoading || !promoCode.trim()}
                      className="h-9 px-3 rounded-xl bg-[#0C63BF] text-white text-xs font-bold hover:bg-[#033871] disabled:opacity-50 transition-colors"
                      data-testid="promo-apply-btn">
                      {promoLoading ? '...' : t('co.apply')}
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#5A8BA8]">{t('co.item')}</span>
                  {acceptedOffer ? (
                    <div className="text-right">
                      <span className="text-xs text-[#B8D4E3] line-through mr-2">{listing.price.toFixed(2)}&euro;</span>
                      <span className="font-semibold text-emerald-600" data-testid="checkout-negotiated-price">{acceptedOffer.amount.toFixed(2)}&euro;</span>
                    </div>
                  ) : (
                    <span className="font-semibold text-[#033871]">{listing.price.toFixed(2)}&euro;</span>
                  )}
                </div>
                {acceptedOffer && (
                  <div className="flex justify-between" data-testid="negotiated-badge">
                    <span className="text-emerald-600 text-xs">{t('co.negotiated')}</span>
                    <span className="font-semibold text-emerald-600 text-xs">-{(listing.price - acceptedOffer.amount).toFixed(2)}&euro;</span>
                  </div>
                )}
                {promoDiscount > 0 && (
                  <div className="flex justify-between" data-testid="promo-discount-line">
                    <span className="text-emerald-600 flex items-center gap-1"><Tag className="w-3 h-3" /> {t('co.discount')}</span>
                    <span className="font-semibold text-emerald-600">-{promoDiscount.toFixed(2)}&euro;</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#5A8BA8]">{t('co.shipping')}</span>
                  <span className="font-semibold text-[#033871]">
                    {selectedShipping ? `${shippingCost.toFixed(2)}\u20AC` : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5A8BA8] flex items-center gap-1">
                    <Shield className="w-3 h-3 text-[#32CD32]" /> {t('co.protection')}
                  </span>
                  <span className="font-semibold text-[#033871]">{protectionFee.toFixed(2)}&euro;</span>
                </div>
                <div className="border-t border-[#E8F4FD] pt-3 flex justify-between">
                  <span className="font-bold text-[#033871]">{t('co.total')}</span>
                  <span className="font-black text-xl text-[#0C63BF]" data-testid="checkout-total">{totalPrice.toFixed(2)}&euro;</span>
                </div>

                {/* Wallet payment option */}
                {walletBalance > 0 && (
                  <div className="border border-[#D6E8F0] rounded-xl p-3 mt-1" data-testid="wallet-payment-section">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useWallet}
                        onChange={e => setUseWallet(e.target.checked)}
                        className="w-4 h-4 rounded border-[#D6E8F0] text-[#0C63BF] focus:ring-[#0C63BF]"
                        data-testid="use-wallet-checkbox"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-[#033871] flex items-center gap-1.5">
                          <Wallet className="w-4 h-4 text-[#0C63BF]" /> {t('co.use_wallet')}
                        </p>
                        <p className="text-xs text-[#5A8BA8]">{t('co.balance')} <span className="font-bold text-[#0C63BF]">{walletBalance.toFixed(2)}&euro;</span></p>
                      </div>
                    </label>
                    {useWallet && walletUsed > 0 && (
                      <div className="mt-2 pt-2 border-t border-[#E8F4FD] space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-[#5A8BA8]">{t('co.wallet')}</span>
                          <span className="font-bold text-emerald-600">-{walletUsed.toFixed(2)}&euro;</span>
                        </div>
                        {stripeAmount > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-[#5A8BA8]">{t('co.remaining_card')}</span>
                            <span className="font-bold text-[#033871]">{stripeAmount.toFixed(2)}&euro;</span>
                          </div>
                        )}
                        {stripeAmount <= 0 && (
                          <p className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                            <CheckCircle className="w-3.5 h-3.5" /> {t('co.fully_covered')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button onClick={handlePay} disabled={buying || !isFormValid}
                className="w-full mt-5 bg-[#32CD32] hover:bg-[#28a428] h-12 text-base rounded-xl font-bold shadow-lg shadow-[#32CD32]/20 disabled:opacity-50"
                data-testid="checkout-pay-btn">
                {buying ? (
                  <><Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('co.redirecting')}</>
                ) : stripeAmount <= 0 && useWallet ? (
                  <><Wallet className="w-4 h-4 mr-2" /> {t('co.pay_wallet')}</>
                ) : (
                  <><Lock className="w-4 h-4 mr-2" /> {t('co.pay')} {stripeAmount > 0 && useWallet ? `${stripeAmount.toFixed(2)}€ ${t('co.by_card')}` : `${totalPrice.toFixed(2)}€`}</>
                )}
              </Button>

              <div className="mt-4 flex items-start gap-2 text-[10px] text-[#5A8BA8]">
                <Shield className="w-4 h-4 text-[#32CD32] shrink-0 mt-0.5" />
                <p>{t('co.secure_note')}</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
